import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle, IconPlus } from "@tabler/icons-react";
import { notifyError } from "../../utils/notify";
import classes from "./ServicePostAppointmentSection.module.css";
import { useFormTemplates } from "../../context/FormTemplatesContext";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useServicePostAppointment } from "../../context/ServicePostAppointmentContext";
import { SalonDocument, documentDisplayName } from "../../types/salonDocument";
import {
  DEFAULT_POST_APPOINTMENT_SETTINGS,
  PostAppointmentAttachment,
  PostAppointmentAttachmentPayload,
  PostAppointmentMessage,
  PostAppointmentSettings,
  PendingPostAppointmentAttachment,
  PendingPostAppointmentMessage,
  describeOffset,
} from "../../types/servicePostAppointment";
import PostAppointmentMessageModal, {
  PostAppointmentMessageDraft,
  seedAttachmentsFromMessage,
} from "./PostAppointmentMessageModal";
import PostAppointmentMessageRow from "./PostAppointmentMessageRow";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

interface ServicePostAppointmentSectionProps {
  salonServiceId: string;
  readOnly?: boolean;
  /** New-service mode: parent owns pending state, replayed after service creation. */
  pendingSettings?: PostAppointmentSettings;
  pendingMessages?: PendingPostAppointmentMessage[];
  onPendingSettingsChange?: (settings: PostAppointmentSettings) => void;
  onAddPendingMessage?: (message: PendingPostAppointmentMessage) => void;
  onUpdatePendingMessage?: (
    tempId: string,
    message: PendingPostAppointmentMessage,
  ) => void;
  onRemovePendingMessage?: (tempId: string) => void;
}

type AnyMessage =
  | { kind: "saved"; message: PostAppointmentMessage }
  | { kind: "pending"; message: PendingPostAppointmentMessage };

function messageOffsetMinutes(m: AnyMessage): number {
  if (m.kind === "saved") return m.message.offset_minutes;
  const perUnit = m.message.offset_unit === "weeks" ? 60 * 24 * 7 : 60 * 24;
  return m.message.offset_value * perUnit;
}

function pendingToDisplay(
  p: PendingPostAppointmentMessage,
): PostAppointmentMessage {
  return {
    uuid: p.tempId,
    offset_value: p.offset_value,
    offset_unit: p.offset_unit,
    label: p.label,
    sort_order: p.sort_order,
    offset_minutes:
      (p.offset_unit === "weeks" ? 60 * 24 * 7 : 60 * 24) * p.offset_value,
    attachments: p.attachments.map((a) => ({
      uuid: a.tempId,
      source_type: a.source_type,
      source_id: a.source_id,
      sort_order: a.sort_order,
    })) as PostAppointmentAttachment[],
  };
}

export const ServicePostAppointmentSection: React.FC<
  ServicePostAppointmentSectionProps
> = ({
  salonServiceId,
  readOnly = false,
  pendingSettings,
  pendingMessages,
  onPendingSettingsChange,
  onAddPendingMessage,
  onUpdatePendingMessage,
  onRemovePendingMessage,
}) => {
  const isNew = !salonServiceId;
  const { standardTemplates, customTemplates, fetchTemplates } =
    useFormTemplates();
  const { listSalonDocuments } = useApp() as {
    listSalonDocuments: (
      salonId: string,
    ) => Promise<{ documents: SalonDocument[] }>;
  };
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };
  const {
    getSettings,
    updateSettings,
    createMessage,
    updateMessage,
    deleteMessage,
  } = useServicePostAppointment();

  const [settings, setSettings] = useState<PostAppointmentSettings>(
    DEFAULT_POST_APPOINTMENT_SETTINGS,
  );
  const [salonDocuments, setSalonDocuments] = useState<SalonDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingSwitch, setSavingSwitch] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalSaving, setMessageModalSaving] = useState(false);
  const [messageModalInitial, setMessageModalInitial] = useState<
    Partial<PostAppointmentMessageDraft> | undefined
  >(undefined);
  const [editingSavedUuid, setEditingSavedUuid] = useState<string | null>(null);
  const [editingPendingTempId, setEditingPendingTempId] = useState<
    string | null
  >(null);

  const [pendingDelete, setPendingDelete] = useState<{
    kind: "saved" | "pending";
    id: string;
    name: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const data = await getSettings(salonServiceId);
        if (active) setSettings(data);
      } catch (err) {
        console.error("Failed to load post-appointment settings:", err);
        if (active) setLoadError(true);
      } finally {
        if (active) setLoading(false);
      }
    })();
    fetchTemplates().catch(() => {});
    return () => {
      active = false;
    };
  }, [isNew, salonServiceId, getSettings, fetchTemplates]);

  // `listSalonDocuments` from AppContext is a fresh reference on every provider
  // render (the value object isn't memoized), so keep it in a ref and fetch in a
  // separate effect keyed only on the salon id. Depending on the function
  // directly would re-run on every AppProvider render — flickering the spinner
  // and clobbering local edits with stale server data.
  const listSalonDocumentsRef = useRef(listSalonDocuments);
  listSalonDocumentsRef.current = listSalonDocuments;

  useEffect(() => {
    if (isNew) return;
    const salonId = businessInfo?.id;
    if (!salonId) return;
    let active = true;
    // Load salon documents so saved `salon_document` attachments can resolve to
    // their human-readable name (mirrors how templates are resolved). Failure
    // here must never block the settings panel — fall back to the UUID label.
    listSalonDocumentsRef
      .current(salonId)
      .then((res) => {
        if (active) {
          setSalonDocuments((res?.documents ?? []).filter((d) => !d.clientId));
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [isNew, businessInfo?.id]);

  const current = isNew
    ? (pendingSettings ?? DEFAULT_POST_APPOINTMENT_SETTINGS)
    : settings;

  const combinedMessages: AnyMessage[] = useMemo(() => {
    const saved = isNew
      ? []
      : settings.messages.map<AnyMessage>((message) => ({
          kind: "saved",
          message,
        }));
    const pend = (isNew ? (pendingMessages ?? []) : []).map<AnyMessage>(
      (message) => ({ kind: "pending", message }),
    );
    return [...saved, ...pend].sort(
      (a, b) => messageOffsetMinutes(a) - messageOffsetMinutes(b),
    );
  }, [isNew, pendingMessages, settings.messages]);

  const resolveAttachmentName = useCallback(
    (a: PostAppointmentAttachment): string => {
      if (a.source_type === "template") {
        const t = standardTemplates.find(
          (x) => (x.slug || x.id) === a.source_id,
        );
        return t?.name ?? `Standard form (${a.source_id})`;
      }
      if (a.source_type === "salon_template") {
        const t = customTemplates.find((x) => (x.slug || x.id) === a.source_id);
        return t?.name ?? `Custom form (${a.source_id})`;
      }
      const doc = salonDocuments.find((d) => d.documentUuid === a.source_id);
      return doc ? documentDisplayName(doc) : `Document (${a.source_id})`;
    },
    [standardTemplates, customTemplates, salonDocuments],
  );

  const writeSettings = useCallback(
    async (next: PostAppointmentSettings) => {
      if (isNew) {
        onPendingSettingsChange?.(next);
        return;
      }
      setSettings(next);
      try {
        const persisted = await updateSettings(salonServiceId, {
          master_enabled: next.master_enabled,
        });
        // updateSettings response may omit messages; preserve working list.
        setSettings((prev) => ({ ...persisted, messages: prev.messages }));
      } catch (err) {
        console.error("Failed to update post-appointment settings:", err);
        notifyError("Your change was not saved. Please try again.", {
          title: "Could not update post-appointment messages",
        });
        try {
          const fresh = await getSettings(salonServiceId);
          setSettings(fresh);
        } catch {
          /* ignore */
        }
      }
    },
    [
      isNew,
      salonServiceId,
      updateSettings,
      getSettings,
      onPendingSettingsChange,
    ],
  );

  const handleToggleMaster = async (checked: boolean) => {
    setSavingSwitch(true);
    try {
      await writeSettings({ ...current, master_enabled: checked });
    } finally {
      setSavingSwitch(false);
    }
  };

  const openAddMessage = () => {
    setEditingSavedUuid(null);
    setEditingPendingTempId(null);
    setMessageModalInitial(undefined);
    setMessageModalOpen(true);
  };

  const openEditMessage = (anyMessage: AnyMessage) => {
    if (anyMessage.kind === "saved") {
      setEditingSavedUuid(anyMessage.message.uuid);
      setEditingPendingTempId(null);
      setMessageModalInitial({
        offset_value: anyMessage.message.offset_value,
        offset_unit: anyMessage.message.offset_unit,
        label: anyMessage.message.label ?? "",
        attachments: seedAttachmentsFromMessage(
          anyMessage.message.attachments,
          resolveAttachmentName,
        ),
      });
    } else {
      setEditingSavedUuid(null);
      setEditingPendingTempId(anyMessage.message.tempId);
      setMessageModalInitial({
        offset_value: anyMessage.message.offset_value,
        offset_unit: anyMessage.message.offset_unit,
        label: anyMessage.message.label ?? "",
        attachments: anyMessage.message.attachments,
      });
    }
    setMessageModalOpen(true);
  };

  const handleMessageSave = async (
    draft: PostAppointmentMessageDraft,
    payload: { attachments: PostAppointmentAttachmentPayload[] },
  ) => {
    setMessageModalSaving(true);
    try {
      if (isNew) {
        const pendingAttachments: PendingPostAppointmentAttachment[] =
          draft.attachments.map((a, i) => ({
            tempId: a.tempId,
            source_type: a.source_type,
            source_id: a.source_id,
            name: a.name,
            sort_order: i,
          }));
        if (editingPendingTempId) {
          onUpdatePendingMessage?.(editingPendingTempId, {
            tempId: editingPendingTempId,
            offset_value: draft.offset_value,
            offset_unit: draft.offset_unit,
            label: draft.label.trim() || null,
            sort_order: 0,
            attachments: pendingAttachments,
          });
        } else {
          const tempId =
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `tmp-${Math.random().toString(36).slice(2)}`;
          onAddPendingMessage?.({
            tempId,
            offset_value: draft.offset_value,
            offset_unit: draft.offset_unit,
            label: draft.label.trim() || null,
            sort_order: 0,
            attachments: pendingAttachments,
          });
        }
        setMessageModalOpen(false);
        return;
      }
      if (editingSavedUuid) {
        const updated = await updateMessage(salonServiceId, editingSavedUuid, {
          offset_value: draft.offset_value,
          offset_unit: draft.offset_unit,
          label: draft.label.trim() || null,
          attachments: payload.attachments,
        });
        setSettings((prev) => ({
          ...prev,
          messages: prev.messages.map((m) =>
            m.uuid === editingSavedUuid ? updated : m,
          ),
        }));
      } else {
        const created = await createMessage(salonServiceId, {
          offset_value: draft.offset_value,
          offset_unit: draft.offset_unit,
          label: draft.label.trim() || null,
          attachments: payload.attachments,
        });
        setSettings((prev) => ({
          ...prev,
          messages: [...prev.messages, created],
        }));
      }
      setMessageModalOpen(false);
    } catch (err) {
      console.error("Failed to save post-appointment message:", err);
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not save post-appointment message",
      });
    } finally {
      setMessageModalSaving(false);
    }
  };

  const askDelete = (anyMessage: AnyMessage) => {
    if (anyMessage.kind === "saved") {
      setPendingDelete({
        kind: "saved",
        id: anyMessage.message.uuid,
        name:
          anyMessage.message.label?.trim() ||
          describeOffset(
            anyMessage.message.offset_value,
            anyMessage.message.offset_unit,
          ),
      });
    } else {
      setPendingDelete({
        kind: "pending",
        id: anyMessage.message.tempId,
        name:
          anyMessage.message.label?.trim() ||
          describeOffset(
            anyMessage.message.offset_value,
            anyMessage.message.offset_unit,
          ),
      });
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.kind === "pending") {
        onRemovePendingMessage?.(pendingDelete.id);
      } else {
        await deleteMessage(salonServiceId, pendingDelete.id);
        setSettings((prev) => ({
          ...prev,
          messages: prev.messages.filter((m) => m.uuid !== pendingDelete.id),
        }));
      }
      setPendingDelete(null);
    } catch (err) {
      console.error("Failed to delete post-appointment message:", err);
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not delete message",
      });
    } finally {
      setDeleting(false);
    }
  };

  const masterEnabled = current.master_enabled;

  return (
    <Stack gap="xs" className={classes.root}>
      <Accordion
        variant="default"
        value={masterEnabled ? "post-appointment" : null}
        // Expand is bound to masterEnabled — chevron click is a no-op when off.
        onChange={() => {
          /* no-op */
        }}
      >
        <Accordion.Item value="post-appointment">
          <Group
            justify="space-between"
            wrap="nowrap"
            gap="xs"
            px="md"
            py="sm"
            className={classes.header}
          >
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Title order={6}>Post-Appointment Messages</Title>
              <Text size="xs" c="dimmed">
                Send aftercare documents and check-in forms after the
                appointment.
              </Text>
            </Stack>
            <Group gap="xs" wrap="nowrap">
              {(loading || savingSwitch) && <Loader size="xs" />}
              <Switch
                checked={masterEnabled}
                onChange={(e) => handleToggleMaster(e.currentTarget.checked)}
                disabled={readOnly || loading || loadError}
                color="var(--brand-teal)"
                aria-label="Post-appointment messages enabled"
                className={classes.headerSwitch}
              />
            </Group>
          </Group>

          {loadError && (
            <Box px="md" pb="sm">
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="red"
                variant="light"
              >
                Could not load post-appointment message settings. Refresh to try
                again — the switch and panel are disabled until then so saved
                values aren&apos;t overwritten.
              </Alert>
            </Box>
          )}

          {masterEnabled && (
            <Accordion.Panel>
              <Stack gap="md">
                <Box>
                  <Group justify="space-between" align="center" mb="xs">
                    <Text fw={600} size="sm">
                      Post-appointment messages
                    </Text>
                    {!readOnly && (
                      <Button
                        size="xs"
                        variant="default"
                        leftSection={<IconPlus size={14} />}
                        onClick={openAddMessage}
                      >
                        Add post-appointment message
                      </Button>
                    )}
                  </Group>

                  {combinedMessages.length === 0 ? (
                    <Alert
                      color="gray"
                      icon={<IconAlertCircle size={14} />}
                      variant="light"
                    >
                      No post-appointment messages yet. Add one to schedule
                      aftercare documents or check-in forms.
                    </Alert>
                  ) : (
                    <Stack gap="xs">
                      {combinedMessages.map((anyMessage) => {
                        const display =
                          anyMessage.kind === "saved"
                            ? anyMessage.message
                            : pendingToDisplay(anyMessage.message);
                        return (
                          <PostAppointmentMessageRow
                            key={
                              anyMessage.kind === "saved"
                                ? `saved:${anyMessage.message.uuid}`
                                : `pending:${anyMessage.message.tempId}`
                            }
                            message={display}
                            readOnly={readOnly}
                            onEdit={() => openEditMessage(anyMessage)}
                            onDelete={() => askDelete(anyMessage)}
                          />
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Accordion.Panel>
          )}
        </Accordion.Item>
      </Accordion>

      <PostAppointmentMessageModal
        opened={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        onSave={handleMessageSave}
        initial={messageModalInitial}
        isEditing={Boolean(editingSavedUuid || editingPendingTempId)}
        isSaving={messageModalSaving}
      />

      <DeleteConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete post-appointment message"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.name}"? This cannot be undone.`
            : ""
        }
        isLoading={deleting}
      />
    </Stack>
  );
};

export default ServicePostAppointmentSection;
